/**
 * The logic behind dragging a cleaning to another day (CAP-902), apart from
 * any rendering so the date arithmetic and the keyboard steps can be exercised
 * without performing a drag. The components are in `day-drag.tsx`.
 */

import {
  pointerWithin,
  rectIntersection,
  useDraggable,
  type CollisionDetection,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core'
import type { CleaningJob } from '@/api/client'
import {
  localDate,
  localDateTimeToInstant,
  localTimeOfDay,
} from '@/components/cleaning/presentation'

const DAY_PREFIX = 'day:'

/** The droppable id of a day group. */
export const dayDropId = (day: string) => `${DAY_PREFIX}${day}`

export function dayOfDropId(id: string | number): string | null {
  return typeof id === 'string' && id.startsWith(DAY_PREFIX) ? id.slice(DAY_PREFIX.length) : null
}

/**
 * Whether a row offers a handle: the same condition the drawer's "Move the
 * cleaning" section is shown under. A finished, cancelled or not-required job
 * has nothing to move, and a block still waiting on a decision is moved after
 * it.
 */
export function canMoveByDrag(job: Pick<CleaningJob, 'status' | 'needs_decision'>): boolean {
  return job.status === 'scheduled' && !job.needs_decision
}

/**
 * The instant for `job` on `day`: the same wall-clock time in the property's
 * zone, on the other date. Null when it is already on that day (nothing to
 * move) or when either side cannot be read.
 *
 * The zone is the one admin-api put on the job. The page's own lookup (from
 * the properties list) is only the fallback: it can still be loading, have
 * failed, or not list an offboarded property, and it then resolves to Eastern,
 * which would put the time an hour or three out for a house elsewhere.
 */
export function instantOnDay(
  job: Pick<CleaningJob, 'scheduled_at' | 'local_date' | 'time_zone'>,
  day: string,
  fallbackTimeZone: string
): string | null {
  const timeZone = job.time_zone ?? fallbackTimeZone
  const current = job.local_date ?? localDate(job.scheduled_at, timeZone)
  if (!current || current === day) return null
  const time = localTimeOfDay(job.scheduled_at, timeZone)
  if (!time) return null
  return localDateTimeToInstant(day, time, timeZone)
}

/** What a drag carries, so the drop and the announcements need no lookups. */
export interface DragData {
  job: CleaningJob
  propertyName: string
  day: string | null
}

/**
 * A pointer drag lands where the pointer is, and ONLY there: released outside
 * the table it is over nothing and the drop cancels, even if the preview
 * (which is wider than the pointer) still overlaps a day. A keyboard drag has
 * no pointer, so it is read from the dragged row's own rectangle.
 */
export const collisionDetection: CollisionDetection = args =>
  args.pointerCoordinates ? pointerWithin(args) : rectIntersection(args)

/**
 * Arrow keys step one day group at a time.
 *
 * dnd-kit's default moves the dragged row by 25px a press, which across a
 * table of forty rows is not a way to choose a day. Up and Down jump to the
 * neighbouring group instead, placing the dragged row at its top edge, so the
 * collision detection reads it as over that group.
 */
export const dayKeyboardCoordinates: KeyboardCoordinateGetter = (
  event,
  {
    context: { active, collisionRect, droppableRects, droppableContainers, over },
    currentCoordinates,
  }
) => {
  if (event.code !== 'ArrowDown' && event.code !== 'ArrowUp') return undefined
  event.preventDefault()
  if (!active || !collisionRect) return undefined

  const days = droppableContainers
    .getEnabled()
    .filter(container => dayOfDropId(container.id) !== null)
    .map(container => ({ id: container.id, rect: droppableRects.get(container.id) }))
    .filter((entry): entry is { id: string | number; rect: NonNullable<typeof entry.rect> } =>
      Boolean(entry.rect)
    )
    .sort((a, b) => a.rect.top - b.rect.top)
  if (days.length === 0) return undefined

  const here = over ? days.findIndex(entry => entry.id === over.id) : -1
  const step = event.code === 'ArrowDown' ? 1 : -1
  // Not over anything yet (the first press): the nearest group in that
  // direction from where the row sits.
  const next =
    here >= 0
      ? days[here + step]
      : step > 0
        ? days.find(entry => entry.rect.top > collisionRect.top)
        : [...days].reverse().find(entry => entry.rect.top < collisionRect.top)
  if (!next) return undefined

  return { x: currentCoordinates.x, y: next.rect.top + (currentCoordinates.y - collisionRect.top) }
}

/**
 * The draggable half of a row: whether it can be picked up at all, and the
 * drag state. Keyed by the job pair, which is unique on the page.
 */
export function useDragRow(job: CleaningJob, propertyName: string, day: string | null) {
  const draggable = canMoveByDrag(job) && Boolean(day)
  const drag = useDraggable({
    id: `${job.property_id}:${job.id}`,
    data: { job, propertyName, day } satisfies DragData,
    disabled: !draggable,
  })
  return { ...drag, draggable }
}

/**
 * Jobs filed under the day each belongs to, days in calendar order and a job
 * with no day last.
 *
 * By day rather than by runs of equal days: a job moved to another day is
 * written into the list at once (the saved job comes back from admin-api)
 * while the array is still in the old order, so two runs of one day would be
 * two drop targets with the same id until the refetch sorted it out. And the
 * days are sorted for the same reason: Monday, Tuesday, Monday (the moved job
 * back in its old slot) would otherwise draw Tuesday above Monday until then.
 */
export function groupByDay<T>(
  jobs: readonly T[],
  dayOf: (job: T) => string | null
): { day: string | null; jobs: T[] }[] {
  const groups = new Map<string, { day: string | null; jobs: T[] }>()
  for (const job of jobs) {
    const day = dayOf(job)
    const key = day ?? ''
    const group = groups.get(key) ?? { day, jobs: [] }
    group.jobs.push(job)
    groups.set(key, group)
  }
  return [...groups.values()].sort((a, b) =>
    a.day === b.day ? 0 : a.day === null ? 1 : b.day === null ? -1 : a.day < b.day ? -1 : 1
  )
}
